// src/pages/Admin/ScoringGuideAuditTrail.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Container, Row, Col, Form, Badge, Button, Alert } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import TitleWithBack from '../../components/common/TitleWithBack';
import PaginatedTable from '../../components/PaginatedTable';

function formatDuration(seconds) {
  if (seconds == null) return 'In progress';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s` : `${m}m ${String(s).padStart(2, '0')}s`;
}

// Audit trail of a scoring guide's lifecycle (RLP/OSM modules only): who
// created/re-uploaded it, and every time an examiner viewed/scored it - how
// long they had it open - scoped per exam series.
export default function ScoringGuideAuditTrail() {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [examSeriesList, setExamSeriesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ examSeriesId: '', moduleId: '' });

  const scoringGuideModules = useMemo(
    () => modules.filter((m) => m.examType?.name === 'RLP' || m.examType?.name === 'OSM'),
    [modules]
  );

  useEffect(() => {
    Promise.all([axios.get('/api/modules'), axios.get('/api/exam-series')])
      .then(([modRes, seriesRes]) => {
        setModules(Array.isArray(modRes.data) ? modRes.data : (modRes.data.content || []));
        setExamSeriesList(Array.isArray(seriesRes.data) ? seriesRes.data : (seriesRes.data.content || []));
      })
      .catch(() => console.error('Error fetching filter data'));
  }, []);

  const fetchRows = () => {
    setLoading(true);
    setError('');
    const params = {};
    if (filters.examSeriesId) params.examSeriesId = filters.examSeriesId;
    if (filters.moduleId) params.moduleId = filters.moduleId;
    axios
      .get('/api/scoring-guide-audit', { params })
      .then((res) => {
        setRows(res.data || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching audit trail.');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.examSeriesId, filters.moduleId]);

  const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.examSeriesId) params.examSeriesId = filters.examSeriesId;
      if (filters.moduleId) params.moduleId = filters.moduleId;
      const res = await axios.get('/api/scoring-guide-audit/export-pdf', { params, responseType: 'blob' });
      saveAs(res.data, 'scoring-guide-audit-trail.pdf');
    } catch (err) {
      setError('Error generating the PDF export.');
    } finally {
      setExporting(false);
    }
  };

  const actionBadge = (action) => (
    <Badge bg={action === 'CREATED' ? 'primary' : 'success'}>{action === 'CREATED' ? 'Created' : 'Viewed'}</Badge>
  );

  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'module', header: 'Module' },
    { key: 'series', header: 'Exam Series' },
    { key: 'user', header: 'User' },
    { key: 'action', header: 'Action' },
    { key: 'date', header: 'Date' },
    { key: 'time', header: 'Time' },
    { key: 'duration', header: 'Duration' },
  ];

  const renderRow = (r, idx, pageIndex, pageSize) => (
    <>
      <td>{(pageIndex * pageSize) + idx + 1}</td>
      <td><small>{r.moduleCode} - {r.moduleName}</small></td>
      <td><small>{r.examSeriesName || 'N/A'}</small></td>
      <td><small>{r.userName}</small></td>
      <td>{actionBadge(r.action)}</td>
      <td><small>{r.accessDate ? new Date(r.accessDate).toLocaleDateString() : '-'}</small></td>
      <td><small>{r.accessDate ? new Date(r.accessDate).toLocaleTimeString() : '-'}</small></td>
      <td><small>{r.action === 'VIEWED' ? formatDuration(r.durationSeconds) : '-'}</small></td>
    </>
  );

  return (
    <Container className="mt-2" fluid>
      <TitleWithBack title="Scoring Guide Audit Trail" />

      <Row className="mb-3 align-items-end">
        <Col md={4}>
          <Form.Label>Exam Series</Form.Label>
          <Form.Select name="examSeriesId" value={filters.examSeriesId} onChange={handleFilterChange}>
            <option value="">All series</option>
            {examSeriesList.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>Module (RLP/OSM)</Form.Label>
          <Form.Select name="moduleId" value={filters.moduleId} onChange={handleFilterChange}>
            <option value="">All RLP/OSM modules</option>
            {scoringGuideModules.map((m) => (
              <option key={m.id} value={m.id}>{m.moduleCode} - {m.moduleName}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Button variant="outline-secondary" onClick={handleExportPdf} disabled={exporting}>
            {exporting ? 'Preparing PDF...' : 'Export to PDF'}
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <p>Loading...</p>
      ) : rows.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable data={rows} columns={columns} renderRow={renderRow} rowsPerPage={50} />
      )}
    </Container>
  );
}
